import React, { type FC, useState } from 'react';
import { useCreditCheckout, useCredits } from '../../hooks/api/Credits/useCredits';
import { clampQuantity, CreditPackage, formatCents, packagePrice, perUseLabel } from '../../utility/credits';
import { getErrorMessages } from '../../utility/team';

export type BuyCreditsModalProps = {
  onClose: () => void;
}

const inputClass = 'block w-20 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';
const primaryButton = 'rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400';
const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';

// Each stored contract uses 1 credit. Payment happens on Stripe's own page; credits are added as soon as
// Stripe confirms the payment.
const BuyCreditsModal: FC<BuyCreditsModalProps> = ({ onClose }) => {
  const { credits, isLoading, error } = useCredits();
  const checkout = useCreditCheckout();
  const [ quantity, setQuantity ] = useState(1);
  const [ message, setMessage ] = useState<string[]>([]);

  const handleBuy = async (pkg: CreditPackage) => {
    setMessage([]);
    try {
      const response = await checkout.mutateAsync({ packageKey: pkg.key, quantity: pkg.quantity_allowed ? quantity : undefined });
      window.location.href = response.data.url as string;
    } catch (err) {
      setMessage(getErrorMessages(err));
    }
  };

  return (
    <div className='fixed inset-0 z-40 flex items-center justify-center bg-gray-900/50 p-4' data-tag='buy-credits-modal'>
      <div role='dialog' aria-modal='true' aria-label='Buy credits' className='max-h-full w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 text-sm shadow-xl'>
        <div className='flex items-start justify-between'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>Buy contract credits</h2>
            <p className='mt-1 text-gray-600'>Each stored contract uses 1 credit. Previews are free.</p>
          </div>
          <button type='button' className={secondaryButton} onClick={onClose}>Close</button>
        </div>

        {isLoading && <p className='mt-4 text-gray-600'>Loading...</p>}
        {error && <p className='mt-4 text-red-600'>{getErrorMessages(error)[0]}</p>}

        {credits && (
          <>
            <p className='mt-4 font-medium text-gray-900'>Your balance: {credits.balance} credit{credits.balance === 1 ? '' : 's'}</p>
            <ul className='mt-4 divide-y divide-gray-100'>
              {credits.packages.map((pkg) => (
                <li key={pkg.key} className='flex items-center justify-between gap-x-3 py-3'>
                  <div>
                    <p className='font-medium text-gray-900'>{pkg.quantity_allowed ? 'Pay per use' : pkg.label}</p>
                    <p className='text-gray-600'>
                      {pkg.quantity_allowed
                        ? `${formatCents(pkg.price_cents)} each`
                        : `${formatCents(pkg.price_cents)} (${perUseLabel(pkg.credits, pkg.price_cents)})`}
                    </p>
                  </div>
                  <div className='flex items-center gap-x-2'>
                    {pkg.quantity_allowed && (
                      <input
                        aria-label='How many uses'
                        type='number'
                        min={1}
                        max={pkg.max_quantity ?? 99}
                        className={inputClass}
                        value={quantity}
                        onChange={(e) => setQuantity(clampQuantity(Number(e.target.value), pkg.max_quantity))}
                      />
                    )}
                    <button type='button' className={primaryButton} disabled={checkout.isPending} onClick={() => handleBuy(pkg)}>
                      Buy {pkg.quantity_allowed ? `${quantity} for ${formatCents(packagePrice(pkg, quantity))}` : pkg.label}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <p className='mt-4 text-gray-600'>
              Need 1,000 or more? Email <a className='text-indigo-600 underline' href={`mailto:${credits.support_email}`}>{credits.support_email}</a> for custom pricing.
            </p>
          </>
        )}

        {message.length > 0 && <ul role='alert' className='mt-3 text-sm text-red-600'>{message.map((m) => <li key={m}>{m}</li>)}</ul>}
      </div>
    </div>
  );
};

export default BuyCreditsModal;
